import React, { useState, useContext } from 'react';
import {
  FilterDropdown,
  VirtuallyHidden,
  Button,
  useMedia,
  Icon,
} from '@monash/portal-react';
import { AccessibilityContext } from '@monash/portal-frontend-common';
import { SearchContext } from '../../providers/search-provider/SearchProvider';
import PageContainer from '../../layouts/page-container/PageContainer';
import dummyLinks from '../../utilities/data/dummy-links.json';
import SearchResultsCard from '../../ui/search-result-card/SearchResultsCard';
import filterLinksByCategory from '../../utilities/filter-and-sort/filterLinksByCategory';
import SearchError from '../search/SearchError';
import { getAvailableFilters } from '../category/utils/get-available-filters';
import { linkMatchesFilters } from '../category/utils/link-matches-filters';
import { getDropdownFilterListForDesktop } from '../category/utils/get-dropdown-filter-list-for-desktop';
import { FilterModal } from './FilterModal';
import { getDropdownFilterList } from '../category/utils/get-dropdown-filter-list';
import c from './all-links.module.scss';
import { CLICK_TYPE } from './constants';
import { getClickedFilterName } from './utils/get-clicked-filter-name';

const AllLinks = () => {
  const { setAppLivePoliteMsgWithReset } = useContext(AccessibilityContext);
  const {
    allLinks: allLinksFromContext,
    allCategories,
    loadingLinks,
    searchError,
  } = useContext(SearchContext);
  const TABS_ID_PREFIX = 'links';

  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [selectedCohortValues, setSelectedCohortValues] = useState([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const pageSize = useMedia();
  const isMobile = pageSize === 'XS' || pageSize === 'S';
  const hasFiltersApplied =
    selectedCategoryIds.length > 0 || selectedCohortValues.length > 0;

  const categoryFilters = allCategories.map((category) => ({
    value: category.id,
    text: category.title,
  }));

  const allLinks = allLinksFromContext.map((link) => ({
    ...link,
    ...link.entityContent,
  }));

  const availableCohortFilters = getAvailableFilters(allLinks);
  const cohortDropdownFilters = getDropdownFilterList(availableCohortFilters);
  const cohortDropdownFiltersDesktop = getDropdownFilterListForDesktop(
    availableCohortFilters
  );

  const categoryFilteredLinks = filterLinksByCategory(
    selectedCategoryIds,
    allLinks
  );

  const filteredLinks =
    selectedCohortValues.length > 0
      ? categoryFilteredLinks.filter((link) =>
          linkMatchesFilters(link, selectedCohortValues)
        )
      : categoryFilteredLinks;

  const displayLinks = loadingLinks ? dummyLinks : filteredLinks;

  return (
    <PageContainer type="default">
      {searchError && <SearchError />}
      {!searchError && (
        <div>
          <VirtuallyHidden>
            <h1 tabIndex={-1}>All links</h1>
          </VirtuallyHidden>

          <div
            className={c.contentContainer}
            id={`${TABS_ID_PREFIX}-tabpanel`}
            tabIndex="0"
          >
            <div className={c.contentHeader}>
              <div className={c.filtersWrapper}>
                {!isMobile ? (
                  <>
                    <span className={c.filtersLabel}>Filters</span>
                    <FilterDropdown
                      placeholder="Categories"
                      filters={categoryFilters}
                      selectedFilter={selectedCategoryIds}
                      setSelectedFilter={(newFilters) => {
                        const clickType =
                          selectedCategoryIds.length > newFilters.length
                            ? CLICK_TYPE.DESELECT
                            : CLICK_TYPE.SELECT;

                        setSelectedCategoryIds(newFilters);

                        if (clickType === CLICK_TYPE.SELECT) {
                          const filterName = getClickedFilterName({
                            prevFilters: selectedCategoryIds,
                            newFilters,
                            filtersWithIds: categoryFilters,
                          });
                          setAppLivePoliteMsgWithReset(
                            `${filterName} selected, ${newFilters.length} items selected`
                          );
                        } else if (clickType === CLICK_TYPE.DESELECT) {
                          const filterName = getClickedFilterName({
                            prevFilters: newFilters,
                            newFilters: selectedCategoryIds,
                            filtersWithIds: categoryFilters,
                          });
                          setAppLivePoliteMsgWithReset(
                            `${filterName} deselected, ${newFilters.length} items selected`
                          );
                        }
                      }}
                      align="left"
                      multi
                    />

                    <FilterDropdown
                      placeholder="Location and cohort"
                      filters={cohortDropdownFiltersDesktop}
                      selectedFilter={selectedCohortValues}
                      setSelectedFilter={(newSelectedCohortIds) => {
                        const clickType =
                          selectedCohortValues.length >
                          newSelectedCohortIds.length
                            ? CLICK_TYPE.DESELECT
                            : CLICK_TYPE.SELECT;

                        setSelectedCohortValues(newSelectedCohortIds);

                        if (clickType === CLICK_TYPE.SELECT) {
                          const filterName = getClickedFilterName({
                            prevFilters: selectedCohortValues,
                            newFilters: newSelectedCohortIds,
                            filtersWithIds: cohortDropdownFilters,
                            key: 'rawText',
                          });

                          setAppLivePoliteMsgWithReset(
                            `${filterName} selected, ${newSelectedCohortIds.length} items selected`
                          );
                        } else if (clickType === CLICK_TYPE.DESELECT) {
                          const filterName = getClickedFilterName({
                            prevFilters: newSelectedCohortIds,
                            newFilters: selectedCohortValues,
                            filtersWithIds: cohortDropdownFilters,
                            key: 'rawText',
                          });

                          setAppLivePoliteMsgWithReset(
                            `${filterName} deselected, ${newSelectedCohortIds.length} items selected`
                          );
                        }
                      }}
                      align="left"
                      className={c.cohortFilter}
                      multi
                    />

                    {hasFiltersApplied && (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => {
                          setSelectedCategoryIds([]);
                          setSelectedCohortValues([]);
                        }}
                      >
                        Clear filters
                      </Button>
                    )}
                  </>
                ) : (
                  <div className={c.mobileFilterContainer}>
                    <Button
                      size="small"
                      variant="text"
                      icon={<Icon.Filter />}
                      iconPosition="right"
                      onClick={() => {
                        setShowFilterModal(!showFilterModal);
                      }}
                    >
                      Filters
                    </Button>
                  </div>
                )}
              </div>
              <span tabIndex="-1">{displayLinks.length} links</span>
            </div>

            <FilterModal
              showModal={showFilterModal}
              setShowModal={setShowFilterModal}
              categoryFilters={categoryFilters}
              selectedCategoryIds={selectedCategoryIds}
              setSelectedCategoryIds={setSelectedCategoryIds}
              cohortDropdownFilters={cohortDropdownFilters}
              selectedCohortValues={selectedCohortValues}
              setSelectedCohortValues={setSelectedCohortValues}
            />

            <div className={c.links}>
              {displayLinks.map((item, i) => (
                <SearchResultsCard
                  entity={item.entityContent}
                  key={i}
                  index={i}
                  loading={Boolean(loadingLinks)}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default AllLinks;
